/**
 * Where PF-5a invoices show up outside their own pages:
 *
 *   PfTicketInvoiceLink   on a customer-approved field ticket — "Ready to
 *                         invoice" when it is not billed yet, or the invoice
 *                         that holds it. The ticket payload has NO invoice
 *                         field, so the holder is DERIVED (pfHoldingInvoice)
 *                         from the customer's invoices, and the page says so.
 *   PfJobInvoicesSection  on a job's Tickets tab — the invoices whose job is
 *                         this one. An invoice billing tickets from several
 *                         jobs carries no job, so it is not listed here, and
 *                         the panel says that too.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { usePfBillable, usePfInvoiceDetails, usePfInvoices } from '../../../hooks/usePfInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryLoadFailure } from '../../../lib/load-failure';
import { pfDay } from '../../../lib/pipeline-facility';
import { pfHoldingInvoice, pfInvoiceStatusSkin } from '../../../lib/pf-invoices';
import type { PfInvoiceRow } from '../../../types/pf-invoices';
import { useOpenRecord } from '../../downhole/recordPage';
import { PfBadge, PfEmpty, PfPanel, PfRegister, type PfColumn } from '../PfBits';
import { READY_PATH } from './InvoicesScreen';

export function PfTicketInvoiceLink({ ticketId, customerId }: { ticketId: string; customerId: string | null }) {
  const billable = usePfBillable();
  const groups = billable.data?.data ?? [];
  const ready = groups.some((g) => g.tickets.some((t) => t.ticketId === ticketId));
  const billableKnown = !billable.isLoading && !queryLoadFailure(billable);
  const list = usePfInvoices(customerId ? { customer_id: customerId } : undefined, billableKnown && !ready && !!customerId);
  const candidates = (list.data?.data ?? []).filter((r) => r.status !== 'void').map((r) => r.id);
  const details = usePfInvoiceDetails(candidates, billableKnown && !ready);
  const holder = pfHoldingInvoice(details.map((d) => d.data?.data ?? null), ticketId);
  const loading = billable.isLoading || list.isLoading || details.some((d) => d.isLoading);
  const failed = !!queryLoadFailure(billable) || !!queryLoadFailure(list) || details.some((d) => !!queryLoadFailure(d));

  let body: React.ReactNode;
  if (billable.isLoading) body = 'Checking whether this ticket is invoiced…';
  else if (ready) {
    body = (
      <>
        Not invoiced yet.{' '}
        <Link className="font-bold underline" to={`${READY_PATH}?ticket=${ticketId}`} data-testid="pf-ticket-invoice-ready">
          Invoice it from Ready to invoice
        </Link>
        .
      </>
    );
  } else if (holder) {
    body = (
      <>
        On invoice{' '}
        <Link className="font-mono font-bold underline" to={`/pipeline-facility/invoices/${holder.id}`} data-testid="pf-ticket-invoice-holder">
          {holder.invoiceNumber}
        </Link>{' '}
        ({holder.statusLabel || holder.status}).
      </>
    );
  } else if (loading) body = 'Checking whether this ticket is invoiced…';
  else if (failed) body = 'Whether this ticket is invoiced could not be read.';
  else body = 'This ticket is neither on Ready to invoice nor on a live invoice.';

  return (
    <div role="note" data-testid="pf-ticket-invoice" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
      <p>{body}</p>
      <p className="mt-1 text-xs text-fieldiq-muted">Worked out from the customer's invoices: the ticket itself does not record its invoice.</p>
    </div>
  );
}

const jobInvoiceColumns: PfColumn<PfInvoiceRow>[] = [
  { key: 'number', header: 'Invoice', render: (r) => <span className="font-mono font-extrabold text-white">{r.invoiceNumber || '—'}</span> },
  { key: 'status', header: 'Status', render: (r) => <PfBadge skin={pfInvoiceStatusSkin(r.status)} value={r.status} /> },
  { key: 'date', header: 'Invoice date', render: (r) => pfDay(r.invoiceDate) },
  { key: 'due', header: 'Due', render: (r) => pfDay(r.dueDate) },
  { key: 'total', header: 'Total', align: 'right', render: (r) => <span className="font-mono">{fmtCurrency(r.total, r.currency)}</span> },
];

export function PfJobInvoicesSection({ jobId }: { jobId: string }) {
  const openRecord = useOpenRecord();
  const query = usePfInvoices({ job_id: jobId });
  const rows = query.data?.data ?? [];
  return (
    <PfPanel
      eyebrow="Billed"
      title="Invoices"
      testId="pf-job-invoices"
      footer="An invoice billing tickets from several jobs is not listed here: find it on the Invoices register."
    >
      <PfRegister
        query={query}
        rows={rows}
        columns={jobInvoiceColumns}
        rail={(r) => pfInvoiceStatusSkin(r.status).rail}
        onOpen={(r) => openRecord(`/pipeline-facility/invoices/${r.id}`)}
        subject="this job's invoices"
        testId="pf-job-invoice"
        minWidth="min-w-[640px]"
        empty={<PfEmpty>No invoice bills this job yet.</PfEmpty>}
        card={(r) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{r.invoiceNumber || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">Dated {pfDay(r.invoiceDate)} · {fmtCurrency(r.total, r.currency)}</p>
            </div>
            <PfBadge skin={pfInvoiceStatusSkin(r.status)} value={r.status} />
          </div>
        )}
      />
    </PfPanel>
  );
}
